import { Fragment, type ReactNode } from "react";

// Tiny inline markdown renderer for NPC dialogue and reward strings.
// Supports `**bold**` and `*italic*` only — anything richer should
// upgrade to a real markdown lib. Order matters: scan for `**` before
// `*` so a bold pair isn't shredded into three italics.
//
// Returns a stable array of `<span>`/text fragments; safe to drop into
// any host component.

type Token =
  | { kind: "text"; value: string }
  | { kind: "bold"; value: string }
  | { kind: "italic"; value: string };

export function tokenizeInline(text: string): Token[] {
  const tokens: Token[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    // **bold**
    if (text.startsWith("**", cursor)) {
      const close = text.indexOf("**", cursor + 2);
      if (close !== -1) {
        tokens.push({ kind: "bold", value: text.slice(cursor + 2, close) });
        cursor = close + 2;
        continue;
      }
    }
    // *italic*
    if (text[cursor] === "*") {
      const close = text.indexOf("*", cursor + 1);
      if (close !== -1 && close !== cursor + 1) {
        tokens.push({ kind: "italic", value: text.slice(cursor + 1, close) });
        cursor = close + 1;
        continue;
      }
    }
    // Plain run — read until the next `*` or end.
    const next = text.indexOf("*", cursor);
    const end = next === -1 ? text.length : next;
    if (end > cursor) {
      tokens.push({ kind: "text", value: text.slice(cursor, end) });
    }
    // If `next` is a stray `*` with no matching close, consume it as
    // a literal so the cursor keeps moving.
    if (next === end && next !== -1) {
      tokens.push({ kind: "text", value: "*" });
      cursor = end + 1;
    } else {
      cursor = end;
    }
  }
  return tokens;
}

export function renderInline(text: string): ReactNode[] {
  return tokenizeInline(text).map((t, i) => {
    if (t.kind === "bold") {
      return (
        <strong key={i} style={{ fontWeight: 700 }}>
          {t.value}
        </strong>
      );
    }
    if (t.kind === "italic") {
      return (
        <em key={i} style={{ fontStyle: "italic" }}>
          {t.value}
        </em>
      );
    }
    return <Fragment key={i}>{t.value}</Fragment>;
  });
}
